import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Video, ChevronLeft, ChevronRight, Check, X, CalendarPlus, Download, ExternalLink } from 'lucide-react';
import AccessGate from './AccessGate';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { celebrate } from '../lib/celebrate';
import { toLocalYMD, formatLocalDate } from '../lib/dateHelpers';

// ---------------------------------------------------------------------------
// Calendar dates are LOCAL (Tuesday June 10th) — see lib/dateHelpers for why
// we never use Date.toISOString() or new Date('YYYY-MM-DD') for them.
// ---------------------------------------------------------------------------

const AppointmentsPage = ({ user }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    topic: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);
  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');

  useEffect(() => {
    if (user?.id) {
      checkAccessLevel();
      fetchAvailableSlots();
      fetchMyAppointments();
    }
  }, [user, currentDate]);

  const checkAccessLevel = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/access/level`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessLevel(data);
      }
    } catch (error) {
      console.error('Error checking access level:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      const response = await fetch(
        `${backendUrl}/api/appointments/available-slots?month=${month}&year=${year}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAppointments = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          date: selectedDate,
          time: selectedTime,
          topic: bookingForm.topic,
          description: bookingForm.description
        })
      });

      if (response.ok) {
        const appointmentData = await response.json();
        setShowBookingForm(false);
        setBookedAppointment({
          date: selectedDate,
          time: selectedTime,
          topic: bookingForm.topic,
          email: user?.email || ''
        });
        setShowSuccessModal(true);
        celebrate(0.5);
        setSelectedDate(null);
        setSelectedTime(null);
        setBookingForm({ topic: '', description: '' });
        fetchAvailableSlots();
        fetchMyAppointments();
      } else if (response.status === 403) {
        const error = await response.json();
        alert(error.detail || 'You need to enroll in a mentorship track to book appointments. Guests can only book 1 Q&A session.');
      } else {
        alert('Failed to book appointment. Please try again.');
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('Failed to book appointment. Please check your connection.');
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/${appointmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        toast.success('Appointment cancelled successfully');
        fetchMyAppointments();
        fetchAvailableSlots();
      } else {
        toast.error('Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    }
  };

  // Handler for cancel button on appointment card
  const handleCancelAppointment = (appointmentId) => {
    cancelAppointment(appointmentId);
  };

  // Handler for reschedule request
  const handleRequestReschedule = (appointment) => {
    setRescheduleAppointment(appointment);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
    setShowRescheduleModal(true);
  };

  const submitRescheduleRequest = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error('Please select a new date and time');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/appointments/${rescheduleAppointment.id}/reschedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          new_date: rescheduleDate,
          new_time: rescheduleTime,
          reason: rescheduleReason
        })
      });

      if (response.ok) {
        toast.success('Reschedule request submitted! Pastor Obed will review your request.');
        setShowRescheduleModal(false);
        setRescheduleAppointment(null);
        fetchMyAppointments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit reschedule request');
      }
    } catch (error) {
      console.error('Error submitting reschedule request:', error);
      toast.error('Failed to submit reschedule request');
    }
  };

  const getSlotsForDate = (date) => {
    return availableSlots.filter(slot => slot.date === date);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-orange-100 text-orange-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      alternatives_offered: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || colors.pending;
  };

  // Generate calendar links for confirmed appointments
  const generateCalendarLinks = (appointment) => {
    const startDate = new Date(`${appointment.date}T${appointment.time}:00`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
    
    const formatDateForGoogle = (date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    };

    const formatDateForOutlook = (date) => {
      return date.toISOString();
    };

    const title = encodeURIComponent(`Mentorship Session: ${appointment.topic || 'Meeting'}`);
    const description = encodeURIComponent(
      `Mentorship session with Pastor Obed Ansu\n\n` +
      `Topic: ${appointment.topic || 'Mentorship Session'}\n` +
      (appointment.zoom_link ? `Zoom Link: ${appointment.zoom_link}` : '')
    );
    const location = encodeURIComponent(appointment.zoom_link || 'Online (Zoom)');

    // Generate ICS file content
    const formatICSDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, '').slice(0, -1) + 'Z';
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Obed Ansu Academy//Appointments//EN
BEGIN:VEVENT
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:Mentorship Session: ${appointment.topic || 'Meeting'}
DESCRIPTION:Mentorship session with Pastor Obed Ansu\\n\\nTopic: ${appointment.topic || 'Mentorship Session'}${appointment.zoom_link ? '\\n\\nZoom Link: ' + appointment.zoom_link : ''}
LOCATION:${appointment.zoom_link || 'Online (Zoom)'}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const icsUrl = URL.createObjectURL(blob);

    return {
      google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}&details=${description}&location=${location}`,
      outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${formatDateForOutlook(startDate)}&enddt=${formatDateForOutlook(endDate)}&body=${description}&location=${location}`,
      ics: icsUrl
    };
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  // Show access gate for guests (non-members or those with 1 used session)
  if (accessLevel && !accessLevel.is_mentorship_member && accessLevel.appointments.guest_qa_remaining === 0) {
    return <AccessGate feature="unlimited_appointments" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light text-gray-900 mb-2">Book Academy Appointment</h1>
          <p className="text-gray-600">Schedule a monthly one-on-one session with Pastor Obed Ansu</p>
        </div>

        {/* How It Works Section */}
        <div className="mb-8 bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-6 border border-purple-100">
          <div className="flex items-center space-x-2 mb-4">
            <Video className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">How Appointments Work</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Choose Date & Time</h3>
                <p className="text-sm text-gray-600">Pick a slot that works for you from the calendar below</p>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Get Zoom Link via Email</h3>
                <p className="text-sm text-gray-600">You'll receive a secure Zoom meeting link within 5 minutes</p>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Join From Anywhere</h3>
                <p className="text-sm text-gray-600">Click the link in your email at the scheduled time</p>
              </div>
            </div>
          </div>
          
          {/* Trust Badges */}
          <div className="mt-6 pt-6 border-t border-purple-200 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>🔒 Secure video meetings</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>💻 No downloads required</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>📧 Automatic email reminders</span>
            </div>
          </div>
        </div>

        {/* Monthly Limit Notice */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <div className="text-2xl mr-3">📅</div>
            <div>
              <h3 className="font-semibold text-purple-900">Monthly Appointment Policy</h3>
              <p className="text-sm text-purple-700 mt-1">
                Members can book <strong>one mentorship session per calendar month</strong>. 
                If you need to reschedule due to unforeseen circumstances, please contact the admin.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={previousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                  {day}
                </div>
              ))}
              
              {getDaysInMonth().map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="aspect-square"></div>;
                }

                const dateStr = toLocalYMD(date);
                const slots = getSlotsForDate(dateStr);
                const hasAvailableSlots = slots.some(s => s.available);
                const isPast = date < new Date().setHours(0, 0, 0, 0);
                const isSelected = selectedDate === dateStr;

                return (
                  <button
                    key={dateStr}
                    onClick={() => {
                      if (!isPast && hasAvailableSlots) {
                        setSelectedDate(dateStr);
                        setSelectedTime(null);
                      }
                    }}
                    disabled={isPast || !hasAvailableSlots}
                    className={`aspect-square p-2 rounded-lg text-sm transition-all ${
                      isPast || !hasAvailableSlots
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                        ? 'bg-purple-600 text-white font-semibold'
                        : 'bg-white hover:bg-purple-50 border border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {date.getDate()}
                    {hasAvailableSlots && !isPast && (
                      <div className={`text-xs mt-1 ${isSelected ? 'text-purple-200' : 'text-green-600'}`}>
                        •
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Available Times</h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {getSlotsForDate(selectedDate).map(slot => (
                    <button
                      key={slot.time}
                      onClick={() => {
                        if (slot.available) {
                          setSelectedTime(slot.time);
                          setShowBookingForm(true);
                        }
                      }}
                      disabled={!slot.available}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        !slot.available
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : selectedTime === slot.time
                          ? 'bg-purple-600 text-white'
                          : 'bg-white border border-gray-300 hover:border-purple-500 text-gray-700'
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* My Appointments */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">My Appointments</h3>
              
              {myAppointments.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-10"
                >
                  <motion.div
                    animate={{ rotate: [0, -6, 6, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    className="inline-block"
                  >
                    <Calendar className="w-12 h-12 text-purple-200 mx-auto mb-3" />
                  </motion.div>
                  <p className="text-gray-700 text-sm font-medium">Your calendar is open.</p>
                  <p className="text-gray-500 text-xs mt-1">Pick a date on the left to book your first session.</p>
                </motion.div>
              ) : (
                <div className="space-y-3">
                  {myAppointments.map(apt => {
                    const calendarLinks = apt.status === 'confirmed' ? generateCalendarLinks(apt) : null;
                    
                    return (
                      <div key={apt.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 text-sm">{apt.topic}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {formatLocalDate(apt.date)} at {apt.time}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(apt.status)}`}>
                            {apt.status === 'alternatives_offered' ? 'Choose Date' : apt.status}
                          </span>
                        </div>
                        
                        {apt.zoom_link && apt.status === 'confirmed' && (
                          <a
                            href={apt.zoom_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-sm text-purple-600 hover:text-purple-700 mt-2"
                          >
                            <Video className="w-4 h-4 mr-1" />
                            Join Meeting
                          </a>
                        )}
                        
                        {/* Add to Calendar buttons for confirmed appointments */}
                        {apt.status === 'confirmed' && calendarLinks && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-600 mb-2 flex items-center">
                              <CalendarPlus className="w-3 h-3 mr-1" />
                              Add to Calendar:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              <a
                                href={calendarLinks.google}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700 hover:bg-gray-200"
                              >
                                Google
                              </a>
                              <a
                                href={calendarLinks.outlook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700 hover:bg-gray-200"
                              >
                                Outlook
                              </a>
                              <a
                                href={calendarLinks.ics}
                                download={`appointment-${apt.id}.ics`}
                                className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700 hover:bg-gray-200"
                              >
                                Apple
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Cancel/Reschedule buttons for pending or confirmed appointments */}
                        {(apt.status === 'pending' || apt.status === 'confirmed') && !apt.reschedule_requested && (
                          <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                            <button
                              onClick={() => handleCancelAppointment(apt.id)}
                              className="flex-1 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                              data-testid={`cancel-appointment-btn-${apt.id}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleRequestReschedule(apt)}
                              className="flex-1 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                              data-testid={`reschedule-appointment-btn-${apt.id}`}
                            >
                              Reschedule
                            </button>
                          </div>
                        )}

                        {/* Show pending reschedule request */}
                        {apt.reschedule_requested && apt.reschedule_request && (
                          <div className="mt-3 pt-3 border-t border-gray-100 bg-blue-50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                            <p className="text-xs font-semibold text-blue-900">
                              🔄 Reschedule requested
                            </p>
                            <p className="text-xs text-blue-800 mt-1">
                              Awaiting admin approval to move to{' '}
                              <strong>
                                {formatLocalDate(apt.reschedule_request.new_date, { dateStyle: 'short' })} at {apt.reschedule_request.new_time}
                              </strong>
                            </p>
                          </div>
                        )}
                        
                        {/* Show suggested alternatives if offered */}
                        {apt.status === 'alternatives_offered' && apt.suggested_dates && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-blue-700 font-medium mb-2">
                              Pastor Obed suggested these alternatives:
                            </p>
                            <div className="space-y-1">
                              {apt.suggested_dates.map((sd, i) => (
                                <div key={i} className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
                                  {formatLocalDate(sd.date)} at {sd.time}
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Please contact admin to confirm your preferred date.
                            </p>
                          </div>
                        )}
                        
                        {(apt.status === 'pending') && (
                          <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                            Awaiting approval from Pastor Obed
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Confirm Booking</h2>
            <p className="text-gray-600 mb-6">
              {formatLocalDate(selectedDate)} at {selectedTime}
            </p>
            
            <form onSubmit={handleBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Topic / Reason for Meeting *
                </label>
                <input
                  type="text"
                  required
                  value={bookingForm.topic}
                  onChange={(e) => setBookingForm({ ...bookingForm, topic: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Leadership guidance"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={bookingForm.description}
                  onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  rows="3"
                  placeholder="Any specific topics you'd like to discuss?"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBookingForm(false);
                    setSelectedTime(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-purple-700"
                >
                  Book Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <AnimatePresence>
      {showSuccessModal && bookedAppointment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 18, stiffness: 220 }}
            className="bg-white rounded-2xl max-w-lg w-full p-8"
          >
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: 'spring', damping: 12, stiffness: 200 }}
                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <Check className="w-8 h-8 text-green-600" />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
              <p className="text-gray-600">Your appointment request is pending approval from Pastor Obed</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl p-6 mb-6">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Date & Time</p>
                    <p className="font-semibold text-gray-900">
                      {formatLocalDate(bookedAppointment.date, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} at {bookedAppointment.time}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-semibold text-orange-600">Awaiting Approval</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-orange-900 mb-1">Pending Approval</p>
                  <p className="text-orange-700">
                    Pastor Obed will review your request and send you a Zoom link once approved.
                  </p>
                  <p className="text-orange-600 mt-2 text-xs">
                    ⏱️ You'll be notified via email within 24 hours
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-sm">What happens next:</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">1.</span>
                  <p>Pastor Obed will review your appointment request</p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">2.</span>
                  <p>Once approved, you'll receive a Zoom link via email</p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">3.</span>
                  <p>You'll get a reminder 24 hours before the meeting</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full mt-6 bg-gradient-to-r from-purple-600 to-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-orange-700 transition-all"
            >
              Got It!
            </button>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Reschedule Modal */}
      <AnimatePresence>
      {showRescheduleModal && rescheduleAppointment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="reschedule-modal"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 240 }}
            className="bg-white rounded-xl p-6 max-w-md w-full"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Reschedule Appointment</h2>
            <p className="text-gray-600 text-sm mb-4">
              Current appointment: {formatLocalDate(rescheduleAppointment.date, { dateStyle: 'short' })} at {rescheduleAppointment.time}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={toLocalYMD(new Date())}
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="reschedule-new-date"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Time</label>
                <select
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="reschedule-new-time"
                >
                  <option value="">Select a time</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="15:00">3:00 PM</option>
                  <option value="16:00">4:00 PM</option>
                  <option value="17:00">5:00 PM</option>
                  <option value="18:00">6:00 PM</option>
                  <option value="19:00">7:00 PM</option>
                  <option value="20:00">8:00 PM</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  placeholder="Why do you need to reschedule?"
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="reschedule-reason"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                data-testid="reschedule-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitRescheduleRequest}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="reschedule-submit-btn"
              >
                Submit Request
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default AppointmentsPage;
